"use client";

import React from "react";
import { ShieldCheck, Crosshair, Leaf, HardHat } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheEngineeringMindset() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const keywords = [{
    word: "Capital",
    icon: HardHat
  }, {
    word: "Innovation",
    icon: Leaf
  }, {
    word: "Global",
    icon: Crosshair
  }];
  return <section data-section-id="224942" id="about-sti-group" className="relative bg-background text-foreground overflow-hidden border-y border-border">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-50 dark:opacity-20">
        <div className="absolute inset-0" style={{
        backgroundImage: `linear-gradient(to right, hsl(var(--foreground) / 0.05) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--foreground) / 0.05) 1px, transparent 1px)`,
        backgroundSize: "32px 32px"
      }} />
      </div>

      <div className="container mx-auto relative z-10 px-0 sm:px-4">
        <div className="flex flex-col lg:flex-row min-h-[700px] border-x border-border bg-background/80 backdrop-blur-sm">
          
          {}
          <div className="w-full lg:w-[60%] flex flex-col md:flex-row border-b lg:border-b-0 lg:border-r border-border">
            
            {}
            <div className="hidden md:flex w-16 border-r border-border flex-col items-center justify-between py-12 bg-muted/30">
              <div className="w-px h-24 bg-border" />
              <div className="flex flex-col gap-24 my-8">
                {keywords.map((kw, i) => {
                const Icon = kw.icon;
                return <div key={i} data-index={i} className="flex flex-col items-center gap-4">
                      <Icon className="w-4 h-4 text-primary" />
                      <span className="-rotate-90 whitespace-nowrap font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
                        {kw.word}
                      </span>
                    </div>;
              })}
              </div>
              <div className="w-px h-24 bg-border" />
            </div>

            {}
            <div className="flex md:hidden border-b border-border p-4 justify-between bg-muted/30 overflow-x-auto">
              {keywords.map((kw, i) => {
              const Icon = kw.icon;
              return <div key={i} data-index={i} className="flex items-center gap-2 px-4">
                    <Icon className="w-3 h-3 text-primary" />
                    <span className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
                      {kw.word}
                    </span>
                  </div>;
            })}
            </div>

            {}
            <div ref={textRef} className={`flex-1 p-8 md:p-12 lg:p-20 flex flex-col justify-center transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Badge variant="outline" className="w-fit mb-8 font-mono border-primary/30 text-primary bg-primary/5 uppercase tracking-widest px-3 py-1">
                About STI Group
              </Badge>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold mb-8 tracking-tight text-foreground">
                Singapore Time <br />
                <span className="text-primary">Innovation Investment</span>
              </h2>
              
              <div className="prose prose-slate dark:prose-invert max-w-none text-muted-foreground leading-relaxed font-default">
                <p className="mb-6 text-lg [word-spacing:normal]">
                  STI Group is a diversified multinational investment holding group with business interests spanning private equity, luxury goods, wine, and IT/Internet sectors. We are committed to the dual-engine drive of capital and innovation, using China as a springboard to replicate and expand mature business models, resource networks, and innovative capabilities globally, creating a truly influential international investment platform.
                </p>
                <p className="[word-spacing:normal]">
                  The group owns STI China Group, SCI International Research Institute, Media Division, Finance Division, Internet Division, and Luxury Goods Division. From high-quality project incubation to international think tanks co-built by China and Singapore, STI Group has always stood at the forefront of industrial change, providing comprehensive strategic support and capital empowerment for high-growth enterprises and brands.
                </p>
              </div>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`w-full lg:w-[40%] relative min-h-[400px] lg:min-h-full bg-muted transition-all duration-1000 delay-300 ease-out ${isImageVisible ? "opacity-100" : "opacity-0"}`}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651741/img/sti_group_headquarters_architecture.png" alt="STI Group Global Headquarters" className="absolute inset-0 w-full h-full object-cover grayscale-[10%] contrast-110" />

            {}
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/10 to-transparent mix-blend-multiply" />
            <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />
            
            {}
            <div className="absolute top-6 right-6 flex items-center gap-3 bg-background/90 backdrop-blur-md border border-border px-3 py-1.5 shadow-sm">
              <div className="w-2 h-2 rounded-lg bg-primary animate-pulse" />
              <span className="font-mono text-[10px] text-foreground uppercase tracking-widest">
                Global Hub
              </span>
            </div>

            {}
            <div className="absolute bottom-6 left-6 lg:left-12 flex flex-col gap-1 bg-background/95 backdrop-blur-md border border-border p-4 shadow-xl">
              <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                Investment Status
              </span>
              <span className="font-mono text-sm font-semibold text-foreground">
                PORTFOLIO_ACTIVE
              </span>
              <div className="w-full h-1 bg-muted mt-2">
                <div className="h-full bg-primary w-[92%]" />
              </div>
            </div>

            {}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1/2 hidden lg:flex items-center justify-center w-36 h-36 rounded-lg bg-background border border-border shadow-2xl z-20 group">
              {}
              <div className="absolute inset-2 rounded-lg border border-dashed border-primary/40 animate-[spin_30s_linear_infinite]" />
              {}
              <div className="flex flex-col items-center text-center z-10 transition-transform duration-500 group-hover:scale-110">
                <ShieldCheck className="w-8 h-8 text-primary mb-2" />
                <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-foreground leading-tight">
                  Strategic
                </span>
                <span className="font-mono text-[9px] uppercase text-muted-foreground tracking-wider">
                  Excellence
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}